import React, { useEffect } from 'react';
import { t } from '@lingui/macro';
import { Box, useBreakpoint, useHover } from '@landr/maestro';
import BackgroundImageFromUrl from 'components/BackgroundImageFromUrl/BackgroundImageFromUrl';
import CircleDemoPlayer from 'components/CircleDemoPlayer/CircleDemoPlayer';
import { PlayerProvider } from 'contexts/PlayerContext/PlayerContext';
import { getStreamEntityType } from 'helpers';
import { usePreviewContext } from '../PreviewContext';
import { PreviewHideDelay } from '../constant';
import { _PreviewProps } from '../types';
import { UsePreviewKeyBoardProvider } from '../hooks/keyboardsEvents/usePreviewKeyBoardProvider';
import { usePreviewKeyBoardEvents } from '../hooks/keyboardsEvents/usePreviewKeyBoardEvents';
import * as Styled from './styled';
import PreviewSamples from './PreviewSamples';
import PreviewDetails from './PreviewDetails';

const _Preview = ({
    isPreviewVisible,
    artworkLeft,
    artworkWidth,
    artworkUrl,
    backgroundColor,
    preview,
}: _PreviewProps) => {
    const maxPreviewLength = !useBreakpoint('lg') ? 8 : 4;
    const alt = preview?.entity?.name
        ? t`Artwork of ${preview?.entity.name}`
        : '';

    const { handleKeyDown } = usePreviewKeyBoardEvents(
        isPreviewVisible,
        maxPreviewLength,
    );

    return (
        <Styled.PreviewStyled
            isPreviewVisible={isPreviewVisible}
            data-components="Preview"
            flexDirection="column"
        >
            {isPreviewVisible && (
                <Box onKeyDown={handleKeyDown}>
                    <Styled.PreviewTop artworkLeft={artworkLeft}>
                        <Styled.PreviewTopContainer
                            backgroundColor={backgroundColor}
                        >
                            <Styled.PreviewArtworkPlaceholder
                                artworkWidth={artworkWidth}
                            >
                                <Styled.StyledBackgroundImage>
                                    <BackgroundImageFromUrl
                                        imageUrl={artworkUrl}
                                        fallbackSrc={
                                            '/assets/fallback/artworkPlaceholder.svg'
                                        }
                                        imageSize={250}
                                        alt={alt}
                                    />
                                </Styled.StyledBackgroundImage>
                                {preview && (
                                    <PlayerProvider
                                        playerId={preview.guid}
                                        streamEntityType={getStreamEntityType(
                                            preview.type,
                                        )}
                                    >
                                        <CircleDemoPlayer
                                            entity={preview.entity}
                                            guid={preview.guid}
                                        />
                                    </PlayerProvider>
                                )}
                            </Styled.PreviewArtworkPlaceholder>
                        </Styled.PreviewTopContainer>
                    </Styled.PreviewTop>
                    <Styled.PreviewBottom backgroundColor={backgroundColor}>
                        <Styled.FlexStretch gutter="xxl">
                            <PreviewDetails />
                            <PreviewSamples />
                        </Styled.FlexStretch>
                    </Styled.PreviewBottom>
                </Box>
            )}
        </Styled.PreviewStyled>
    );
};

const usePreview = ({
    isPreviewVisible,
    isOverlayHovered,
    hidePreview,
}: {
    isPreviewVisible: boolean;
    isOverlayHovered: boolean;
    hidePreview: () => void;
}) => {
    useEffect(() => {
        const shouldHidePreview = isPreviewVisible && isOverlayHovered;
        const timeoutId = shouldHidePreview
            ? window.setTimeout(() => hidePreview(), PreviewHideDelay)
            : undefined;

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [isPreviewVisible, isOverlayHovered, hidePreview]);
};

const Preview = () => {
    const [hoverRef, isOverlayHovered] = useHover(false);
    const {
        hidePreview,
        artworkLeft,
        artworkWidth,
        isPreviewVisible,
        preview,
    } = usePreviewContext();

    usePreview({ isPreviewVisible, isOverlayHovered, hidePreview });

    return (
        <UsePreviewKeyBoardProvider>
            <_Preview
                backgroundColor={preview?.backgroundColor ?? '#000000'}
                isPreviewVisible={isPreviewVisible}
                artworkUrl={preview?.artworkUrl}
                artworkLeft={artworkLeft}
                artworkWidth={artworkWidth}
                preview={preview}
            />
            <Styled.PreviewOverlay
                isPreviewVisible={isPreviewVisible}
                ref={hoverRef}
            />
        </UsePreviewKeyBoardProvider>
    );
};

export default Preview;
